/**
 * Email management — sync + filter + preset surface.
 *
 * Mirrors the V1 ``app/components/host.ts`` ``emailManagement`` block plus
 * the typed payloads from ``app/types/email.ts``. Single source of truth
 * for every email-sync interaction the V2 SPA makes against the BE.
 *
 * Wire shapes match the BE handlers in
 * ``ando-agent/src/api/v1/endpoints/email_management.py`` (V1 contract
 * preserved verbatim — no V2-specific renaming).
 */

import { apiClient } from "../ApiClient.js";

// ──────────────────────────────────────────────────────────────────────
// Providers + filters
// ──────────────────────────────────────────────────────────────────────

// ``aruba`` / ``infocert`` = Aruba PEC / InfoCert Legalmail — plain IMAP
// mailboxes that ride the SAME /email-management/* filter endpoints as
// Gmail/Outlook (the BE compiles the filters to IMAP SEARCH criteria and
// resolves each provider's IMAPS host). NOT Gmail/Outlook OAuth providers.
export type EmailProvider = "gmail" | "outlook" | "aruba" | "infocert";

export type EmailTimeRange = "7d" | "30d" | "90d" | "custom";

/**
 * Filter payload accepted by ``POST /email-management/sync-with-filters``.
 *
 * The 90-day ceiling on custom date ranges is enforced server-side
 * (production incident — 12k emails = 19min lock per worker), but the
 * SPA should ALSO enforce it client-side so the user never sees a
 * silent 400 after waiting on the preview spinner.
 */
export interface EmailSyncFilters {
  readonly time_range?: EmailTimeRange;
  /** ISO YYYY-MM-DD. Required when ``time_range === "custom"``. */
  readonly after_date?: string;
  readonly before_date?: string;
  /** Label IDs to include (Gmail) / folder IDs (Outlook). */
  readonly labels?: readonly string[];
  /** Label IDs to exclude — overrides ``labels`` membership. */
  readonly exclude_labels?: readonly string[];
  /** Gmail-only — raw search query (``from:boss has:attachment``). */
  readonly query?: string;
  readonly has_attachment?: boolean;
  readonly is_unread?: boolean;
}

export interface EmailSyncWithFiltersBody {
  readonly user_id: string;
  readonly email_address: string;
  readonly provider: EmailProvider;
  readonly filters: EmailSyncFilters;
  /** Hard ceiling — default omitted = BE-side ``EMAIL_MAX_MESSAGES`` env. */
  readonly max_messages?: number;
}

export interface EmailSyncJobAck {
  readonly job_id?: string | null;
  readonly status?: string;
  readonly detail?: string;
}

/** POST ``/email-management/sync-with-filters`` — main sync entrypoint. */
export async function syncEmailWithFilters(
  body: EmailSyncWithFiltersBody,
): Promise<EmailSyncJobAck> {
  return apiClient.post<EmailSyncJobAck>("/email-management/sync-with-filters", body);
}

// ──────────────────────────────────────────────────────────────────────
// Start / cancel / status
// ──────────────────────────────────────────────────────────────────────

export interface StartSyncBody {
  readonly user_id: string;
  readonly email_address: string;
  readonly provider: EmailProvider;
  /** ``auto`` = incremental resume; ``full`` = re-index everything. */
  readonly sync_type: "auto" | "full";
  /** Skip the resumable checkpoint — start a fresh job. */
  readonly force_new?: boolean;
}

/** POST ``/email-management/start-sync`` — coarse resume / full re-sync. */
export async function startEmailSync(body: StartSyncBody): Promise<EmailSyncJobAck> {
  return apiClient.post<EmailSyncJobAck>("/email-management/start-sync", body);
}

/** POST ``/email-management/cancel-sync?job_id=&user_id=``. */
export async function cancelEmailSync(jobId: string, userId: string): Promise<EmailSyncJobAck> {
  const params = new URLSearchParams({ job_id: jobId, user_id: userId });
  return apiClient.post<EmailSyncJobAck>(`/email-management/cancel-sync?${params.toString()}`, {});
}

export interface EmailSyncStatus {
  readonly status?: string;
  readonly job_id?: string | null;
  readonly job_status?: string | null;
  readonly messages_synced?: number;
  readonly auto_sync_enabled?: boolean;
  readonly next_auto_sync_at?: string | null;
  readonly resumable_job_id?: string | null;
  readonly resumable_job_status?: string | null;
  readonly resumable_messages_processed?: number | null;
  /**
   * Indexed content date range exposed by the BE since March 2026
   * (``email_management.py::get_sync_status``). Mirror of V1's
   * ``EmailHubPage.tsx::EmailSyncStatus.indexed_oldest_date`` shape.
   * Drives the "Indicizzate dal X al Y" line in :class:`EmailSyncSummary`.
   */
  readonly indexed_oldest_date?: string | null;
  readonly indexed_newest_date?: string | null;
  /** Real indexed count from ``content_dedup_index`` (BE single SoT). */
  readonly indexed_count?: number;
  /** Total messages across ALL connections of this user (cross-account aggregate). */
  readonly total_emails_indexed?: number;
  /** Last incremental/full sync timestamp (BE emits whichever ran last). */
  readonly last_incremental_sync?: string | null;
  readonly last_full_sync?: string | null;
  /** 0..100 — drives the inline progress bar while a sync is in flight. */
  readonly progress?: number;
  readonly messages_processed?: number;
  readonly messages_total?: number;
  readonly current_job_id?: string | null;
}

/** GET ``/email-management/sync-status?user_id=&email_address=&provider=``. */
export async function getEmailSyncStatus(
  userId: string,
  emailAddress: string,
  provider: EmailProvider,
): Promise<EmailSyncStatus> {
  const params = new URLSearchParams({
    user_id: userId,
    email_address: emailAddress,
    provider,
  });
  return apiClient.get<EmailSyncStatus>(`/email-management/sync-status?${params.toString()}`);
}

// ──────────────────────────────────────────────────────────────────────
// Pre-sync helpers (preview + labels)
// ──────────────────────────────────────────────────────────────────────

export interface EmailPreviewSyncBody {
  readonly user_id: string;
  readonly email_address: string;
  readonly provider: EmailProvider;
  readonly filters: EmailSyncFilters;
}

export interface EmailPreviewSyncResponse {
  readonly matching_emails: number;
  readonly estimated_sync_time_seconds: number;
  readonly gmail_query?: string;
}

/** POST ``/email-management/preview-sync`` — count + ETA before committing. */
export async function previewEmailSync(
  body: EmailPreviewSyncBody,
): Promise<EmailPreviewSyncResponse> {
  return apiClient.post<EmailPreviewSyncResponse>("/email-management/preview-sync", body);
}

export interface EmailLabel {
  readonly id: string;
  readonly name: string;
  /** Gmail: ``system`` / ``user`` ; Outlook: ``folder``. */
  readonly type: string;
  readonly message_count?: number;
}

export interface EmailLabelsResponse {
  readonly labels: readonly EmailLabel[];
}

/** GET ``/email-management/labels?user_id=&email_address=&provider=``. */
export async function getEmailLabels(
  userId: string,
  emailAddress: string,
  provider: EmailProvider,
): Promise<readonly EmailLabel[]> {
  const params = new URLSearchParams({
    user_id: userId,
    email_address: emailAddress,
    provider,
  });
  const res = await apiClient.get<EmailLabelsResponse>(
    `/email-management/labels?${params.toString()}`,
  );
  return res.labels;
}

// ──────────────────────────────────────────────────────────────────────
// Presets (server-persisted filter snapshots)
// ──────────────────────────────────────────────────────────────────────

export interface EmailFilterPreset {
  readonly id: string;
  readonly preset_name: string;
  readonly filters: EmailSyncFilters;
  readonly is_default?: boolean;
  readonly use_count?: number;
  readonly created_at?: string;
}

export interface EmailPresetsResponse {
  readonly presets: readonly EmailFilterPreset[];
}

/** GET ``/email-management/presets?user_id=&email_address=``. */
export async function listEmailPresets(
  userId: string,
  emailAddress: string,
): Promise<readonly EmailFilterPreset[]> {
  const params = new URLSearchParams({
    user_id: userId,
    email_address: emailAddress,
  });
  const res = await apiClient.get<EmailPresetsResponse>(
    `/email-management/presets?${params.toString()}`,
  );
  return res.presets;
}

export interface SaveEmailPresetBody {
  readonly user_id: string;
  readonly email_address: string;
  readonly preset_name: string;
  readonly filters: EmailSyncFilters;
  readonly is_default?: boolean;
}

/** POST ``/email-management/presets``. */
export async function saveEmailPreset(body: SaveEmailPresetBody): Promise<EmailFilterPreset> {
  return apiClient.post<EmailFilterPreset>("/email-management/presets", body);
}

/** DELETE ``/email-management/presets/{preset_id}``. */
export async function deleteEmailPreset(presetId: string): Promise<void> {
  await apiClient.delete<void>(`/email-management/presets/${encodeURIComponent(presetId)}`);
}
